import { useCallback, useState } from 'react';
import type { Channel } from '@shared/types.ts';
import { player } from '@/lib/player.ts';
import { SourceBusyError } from '@/lib/api.ts';
import { useUi } from '@/stores/ui.ts';
import { useLineup } from '@/stores/lineup.ts';

/**
 * Pressing a channel.
 *
 * The companion to usePlayItem, and extracted for the reason that file's own
 * comment gives: "that reasoning is identical to tuning a channel and should
 * not be re-derived per screen." It was being re-derived per screen the
 * moment a second screen wanted to tune something.
 *
 * Three things happen here that are easy to get subtly wrong in a copy:
 *
 *  - PRESENTATION MOVES FIRST, in the same tick as the press. Waiting for
 *    player.play() means a server round-trip for the descriptor plus hls.js
 *    attaching, and for all of that time the screen looks exactly as it did
 *    before the click. Switching to theater immediately turns dead air into a
 *    black frame saying "Tuning…", which reads as the app responding rather
 *    than ignoring you. Safe to do optimistically because it is only
 *    presentation — nothing here starts or stops media — and the catch puts
 *    it back.
 *  - THE LIST YOU WERE LOOKING AT becomes the list left/right cycles through.
 *    Which list that is depends on the screen, which is exactly why it is a
 *    parameter and not something this hook goes and fetches.
 *  - A BUSY SOURCE IS NOT AN ERROR. SiriusXM allows one stream at a time, and
 *    the server says so with enough detail to offer "listen to that instead".
 *    Routing it to an error boundary would turn an offer into a failure.
 */
export function useTuneChannel(lineup: Channel[] | undefined) {
  const setView = useUi((s) => s.setView);
  const setLineup = useLineup((s) => s.set);
  const [busy, setBusy] = useState<SourceBusyError | null>(null);

  const tune = useCallback(async (channel: Channel) => {
    if (channel.kind === 'tv') setView('theater');
    setLineup(lineup ?? []);

    try {
      await player.play(channel);
    } catch (e) {
      if (channel.kind === 'tv') setView('browse');
      if (e instanceof SourceBusyError) setBusy(e);
    }
  }, [setView, setLineup, lineup]);

  /** Take the offer in the busy dialog: tune what is already playing. */
  const listenInstead = useCallback(async () => {
    const wanted = busy?.active.channelId;
    setBusy(null);
    const target = (lineup ?? []).find((c) => c.id === wanted);
    if (target) await tune(target);
  }, [busy, lineup, tune]);

  const dismissBusy = useCallback(() => setBusy(null), []);

  return { tune, busy, listenInstead, dismissBusy };
}
